import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import { Flame, Gift } from 'lucide-react';

/**
 * FpStreakCard — daily 7-day login-streak widget that drips FP credits.
 *
 * Visually: a row of 7 coins; the next claimable day pulses gold. The
 * claim button is prominent when `can_claim` is true and muted ("Come back
 * tomorrow") after a successful claim.
 *
 * Hides itself gracefully if admin has disabled streaks.
 */
export default function FpStreakCard({ onClaimed }) {
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get(`/api/fp/streak?_=${Date.now()}`, {
        headers: { 'Cache-Control': 'no-cache, no-store' },
      });
      setStatus(r.data);
      return r.data;
    } catch { /* silent — endpoint unavailable */ }
    return null;
  };

  useEffect(() => { refresh(); }, []);

  if (!status || status.enabled === false) return null;

  const schedule = status.schedule || [];
  const currentDay = status.current_day || 1;
  const canClaim = !!status.can_claim;

  const handleClaim = async () => {
    setClaiming(true);
    try {
      const r = await api.post('/api/fp/streak/claim');
      toast.success(r.data?.message || `+${r.data?.reward_fp} FP streak bonus!`);
      setStatus((prev) => prev ? {
        ...prev,
        can_claim: false,
        last_claim_date: new Date().toISOString().slice(0, 10),
        total_claims: (Number(prev.total_claims) || 0) + 1,
        next_claim_at: r.data?.next_claim_at || prev.next_claim_at,
      } : prev);
      await refresh();
      if (onClaimed) await onClaimed(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not claim streak');
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(99,102,241,0.18), rgba(212,175,55,0.08))',
        border: '1px solid rgba(212,175,55,0.25)',
      }}
      data-testid="fp-streak-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-[#F5D76E]" style={{ filter: 'drop-shadow(0 0 6px rgba(245,215,110,0.6))' }} />
          <p className="text-sm font-bold text-white">Daily FP Streak</p>
        </div>
        <p className="text-[10px] uppercase tracking-wider text-[#A5B4FC]" data-testid="fp-streak-total-claims">
          {status.total_claims || 0} claimed
        </p>
      </div>

      {/* 7-day coin row */}
      <div className="grid grid-cols-7 gap-1.5 mb-3" data-testid="fp-streak-schedule">
        {schedule.map((amt, i) => {
          const day = i + 1;
          const isCurrent = day === currentDay;
          const isPast = day < currentDay;
          return (
            <div
              key={day}
              data-testid={`fp-streak-day-${day}`}
              className="flex flex-col items-center justify-center rounded-lg py-2 text-center transition-all"
              style={{
                background: isCurrent
                  ? 'linear-gradient(135deg, rgba(212,175,55,0.28), rgba(245,215,110,0.10))'
                  : isPast
                  ? 'rgba(34,197,94,0.12)'
                  : 'rgba(255,255,255,0.03)',
                border: isCurrent
                  ? '1px solid rgba(245,215,110,0.7)'
                  : isPast
                  ? '1px solid rgba(34,197,94,0.25)'
                  : '1px solid rgba(255,255,255,0.05)',
                boxShadow: isCurrent ? '0 0 14px rgba(245,215,110,0.35)' : 'none',
                animation: isCurrent && canClaim ? 'fp-streak-pulse 1.6s ease-in-out infinite' : 'none',
              }}
            >
              <span className={`text-[9px] uppercase font-semibold ${isCurrent ? 'text-[#F5D76E]' : isPast ? 'text-green-400' : 'text-gray-500'}`}>D{day}</span>
              <span className={`text-[11px] font-bold tabular-nums leading-none mt-0.5 ${isCurrent ? 'text-white' : isPast ? 'text-green-300' : 'text-gray-400'}`}>
                {Math.round(amt)}
              </span>
            </div>
          );
        })}
      </div>

      <style>{`@keyframes fp-streak-pulse { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }`}</style>

      <button
        onClick={handleClaim}
        disabled={!canClaim || claiming}
        data-testid="fp-streak-claim-btn"
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm transition-all hover:scale-[1.01] disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: canClaim
            ? 'linear-gradient(135deg, #D4AF37, #F5D76E)'
            : 'rgba(255,255,255,0.05)',
          color: canClaim ? '#0d1117' : '#A5B4FC',
          border: canClaim ? 'none' : '1px solid rgba(99,102,241,0.25)',
          boxShadow: canClaim ? '0 6px 20px rgba(245,215,110,0.25)' : 'none',
        }}
      >
        <Gift className="w-4 h-4" />
        {claiming
          ? 'Claiming…'
          : canClaim
          ? `Claim +${Math.round(status.reward_fp || 0)} FP · Day ${currentDay}`
          : 'Come back tomorrow for your next streak bonus'}
      </button>
    </div>
  );
}
